import type { Movie } from '@/types/movie';
import { movies as allMovies } from '@/mocks/movies';

const CURRENT_YEAR = 2026;

export type DecadeFilter = 'all' | 'recent' | 'five' | 'classic';
export type SortFilter = 'latest' | 'hot' | 'rating';

export interface MovieQuery {
  keyword?: string;
  genre?: string;
  decade?: DecadeFilter;
  sort?: SortFilter;
}

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return allMovies.find((movie) => movie.id === id);
}

export function getFeaturedMovies(): Movie[] {
  return allMovies.filter((movie) => movie.featured);
}

export function getLatestMovies(limit = 20): Movie[] {
  return [...allMovies]
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...allMovies].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 6): Movie[] {
  const related = allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      const sameDirector = item.director === movie.director ? 2 : 0;
      return { item, score: sharedGenres * 3 + sameDirector };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity)
    .slice(0, limit)
    .map((entry) => entry.item);

  if (related.length >= limit) return related;

  const filler = getHotMovies(limit).filter(
    (item) => item.id !== movie.id && !related.some((r) => r.id === item.id),
  );
  return [...related, ...filler].slice(0, limit);
}

export function getGenreCount(genre: string): number {
  return allMovies.filter((movie) => movie.genres.includes(genre)).length;
}

export function queryMovies({ keyword = '', genre = 'all', decade = 'all', sort = 'latest' }: MovieQuery): Movie[] {
  const trimmed = keyword.trim().toLowerCase();

  let result = allMovies.filter((movie) => {
    if (genre && genre !== 'all' && !movie.genres.includes(genre)) return false;

    if (decade === 'recent' && movie.year < CURRENT_YEAR - 1) return false;
    if (decade === 'five' && movie.year < CURRENT_YEAR - 5) return false;
    if (decade === 'classic' && movie.year >= CURRENT_YEAR - 5) return false;

    if (trimmed) {
      const haystack = [movie.title, movie.director, movie.region, ...movie.genres, ...movie.actors]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(trimmed)) return false;
    }
    return true;
  });

  result = [...result];
  if (sort === 'hot') {
    result.sort((a, b) => b.popularity - a.popularity);
  } else if (sort === 'rating') {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    result.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  }

  return result;
}

export function formatPopularity(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)} 万`;
  return `${value}`;
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} 分钟`;
  return `${h} 小时 ${m} 分`;
}